// language.service.ts
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CountryService } from './country.service';
import { firstValueFrom, Observable, of } from 'rxjs';
import { UeckoCountryDTO } from '../models/country-dto';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  languageBbdd: string[];
  private languagesLoaded = false;
  private languagesLoadedPromise: Promise<void>;
  languageModelList: UeckoCountryDTO[];

  constructor(private translate: TranslateService, private countryService: CountryService) {
    // Guardamos la promesa para poder esperarla desde otros métodos
    this.languagesLoadedPromise = this.loadLanguages().then(() => {
      this.setupTranslation();
      this.languagesLoaded = true;
    });
  }

  /**
   * Método público para esperar a que los idiomas estén completamente cargados
   * @returns Promise que se resuelve cuando los idiomas están listos
   */
  async waitForLanguages(): Promise<void> {
    if (!this.languagesLoaded) {
      await this.languagesLoadedPromise;
    }
  }

  /**
   * Método para verificar si los idiomas están cargados
   * @returns boolean indicando si los idiomas están listos
   */
  areLanguagesReady(): boolean {
    return this.languagesLoaded;
  }

  /**
   * Método para obtener un Observable que emite cuando los idiomas están listos
   * @returns Observable<boolean>
   */
  getLanguagesReadyObservable(): Observable<boolean> {
    if (this.languagesLoaded) {
      return of(true);
    }

    return new Observable(observer => {
      this.languagesLoadedPromise.then(() => {
        observer.next(true);
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  private async loadLanguages(): Promise<void> {
    try {
      this.languageModelList = await firstValueFrom(this.countryService.getActiveLanguageList());

      this.languageBbdd = [];

      if (this.languageModelList.length > 0) {
        this.languageModelList.forEach(elem => {
          if (elem.Language) {
            this.languageBbdd.push(elem.Language);
          }
        });
      }

      if (this.languageBbdd.length === 0) {
        this.languageBbdd.push('es');
      }

      this.languageBbdd = [...new Set(this.languageBbdd)];

    } catch (error) {
      console.error('Error loading languages:', error);
      this.languageBbdd = ['es'];
    }
  }

  private setupTranslation(): void {
    this.translate.addLangs(this.languageBbdd);

    const savedLang = localStorage.getItem('ueckoSelectedLanguage');
    const browserLang = this.translate.getBrowserLang();

    if (savedLang && this.languageBbdd.includes(savedLang)) {
      this.translate.use(savedLang);
    } else if (browserLang && this.languageBbdd.includes(browserLang)) {
      this.translate.use(browserLang);
      localStorage.setItem('ueckoSelectedLanguage', browserLang);
    } else {
      const defaultLang = this.languageBbdd.includes('es') ? 'es' : this.languageBbdd[0];
      this.translate.use(defaultLang);
      localStorage.setItem('ueckoSelectedLanguage', defaultLang);
    }
  }

  changeLanguage(lang: string): void {
    // En lugar de aplicar directamente el idioma,
    // se fuerza la recarga de la página ya que
    // las respuestas de la API vienen en un solo idioma
    // this.translate.use(lang);

    localStorage.setItem('ueckoSelectedLanguage', lang);
    window.location.reload();
  }

  getCurrentLanguage(): string {
    return this.translate.currentLang;
  }

  async getLanguageList() {
    // Esperamos a que los idiomas se carguen si aún no lo han hecho
    if (!this.languagesLoaded) {
      await this.languagesLoadedPromise;
    }

    const languageList = [];
    this.translate.getLangs().forEach(elem => {
      languageList.push({ code: elem, name: elem, flag: elem });
    });
    return languageList;
  }

  getLanguageListAsync() {
    const languageList = [];
    this.translate.getLangs().forEach(elem => {
      let IdLanguage = this.languageModelList.find(o => o.Language == elem).IdCountryInt
      languageList.push({ code: elem, name: elem, flag: elem, idLanguage: IdLanguage });
    });
    return languageList;
  }
}
